/**
 * A choice on a matrix or delta axis can be stored under its choice id, its
 * priceKey or its label. The most specific key that exists on the axis wins.
 *
 * KEEP IN SYNC with smartflow-api/utils/choiceAxis.js.
 */

const CHOICE_TYPES = new Set(['single-choice', 'multi-choice']);

const normalize = (value) => String(value).trim().toLowerCase();

const isPresent = (value) => value !== undefined && value !== null && value !== '';

const choicesOf = (option) =>
  CHOICE_TYPES.has(option?.type) && Array.isArray(option.choiceConfig?.choices)
    ? option.choiceConfig.choices
    : [];

export const choicePriceKey = (choice) =>
  typeof choice?.priceKey === 'string' ? choice.priceKey.trim() : '';

/**
 * Find the choice a selected value or stored axis value refers to: by id first,
 * then by label.
 */
export function findAxisChoice(option, value) {
  if (!isPresent(value)) return null;
  const choices = choicesOf(option);
  return (
    choices.find((choice) => choice?.id === value) ||
    choices.find((choice) => isPresent(choice?.label) && normalize(choice.label) === normalize(value)) ||
    null
  );
}

/**
 * Resolve a selected choice to the value its axis is keyed on: choice id, then
 * priceKey, then label. Without a match the priceKey or label is returned.
 */
export function resolveChoiceAxisValue(option, value, axisValues = []) {
  const choice = findAxisChoice(option, value);
  if (!choice) return value;

  const priceKey = choicePriceKey(choice);
  const present = new Set(axisValues.filter(isPresent).map(normalize));
  const candidates = [choice.id, priceKey, choice.label].filter(isPresent);
  const match = candidates.find((candidate) => present.has(normalize(candidate)));
  return match ?? (priceKey || choice.label);
}

/**
 * Label to show for an axis value: a choice id becomes the choice label.
 */
export function choiceAxisLabel(option, value) {
  if (!isPresent(value)) return value;
  const choice = choicesOf(option).find((entry) => entry?.id === value);
  return choice?.label ?? value;
}

/**
 * True when a stored axis value is a choice id of this option.
 */
export function isChoiceIdAxisValue(option, value) {
  return isPresent(value) && choicesOf(option).some((choice) => choice?.id === value);
}
